import { createSlice } from '@reduxjs/toolkit'
import { get } from 'lodash'
import Status from '@constants/request_statuses.ts'
import { saveOptions } from './actions/ClientActions.js'

const initialState = {
  options: {
    /*
    //Example structure for a view with id 40
    '40': {
      // latest options from the server
      currentOptions: {}, 
      // options that have been changed but not saved
      stagedOptions: {},
      // original values of the options that have been changed. Allows us to determine if a change has been made
      initialChanges: {}, 
    },
  */
  },
  saveOptions: {
    // '40': { status: 'idle', error: null }
  },
}

const viewOptionsSlice = createSlice({
  name: 'viewOptions',
  initialState,
  reducers: {
    initializeView(state, action) {
      const { viewId, options } = action.payload
      const viewIdString = viewId.toString()

      if (state.options[viewIdString]) {
        state.options[viewIdString].currentOptions = { ...options }
      } else {
        state.options[viewIdString] = {
          currentOptions: options,
          stagedOptions: {},
          initialChanges: {},
        }
      }
    },
    stageOptions(state, action) {
      const { viewId, changes } = action.payload
      const viewIdString = viewId.toString()
      if (!state.options[viewIdString]) return
      Object.keys(changes).forEach(key => {
        // If the key is not in initialChanges, set it to the current value
        if (!(key in state.options[viewIdString].initialChanges)) {
          state.options[viewIdString].initialChanges[key] = state.options[viewIdString].currentOptions[key]
        }
        // If the key is in initialChanges and the value is the same as the current value, remove it from stagedOptions
        if (changes[key] === state.options[viewIdString].initialChanges[key]) {
          delete state.options[viewIdString].stagedOptions[key]
          return
        } else {
          state.options[viewIdString].stagedOptions[key] = changes[key]
        }
      })
    },
    resetStagedOptions(state, action) {
      const { viewId } = action.payload
      const viewIdString = viewId.toString()
      if (!state.options[viewIdString]) return

      state.options[viewIdString].stagedOptions = {}
      state.options[viewIdString].initialChanges = {}
    },
    saveOptionsComplete(state, action) {
      const { viewId, options } = action.payload
      const viewIdString = viewId.toString()
      // update the current options with the new options and clear changes/initialChanges
      state.options[viewIdString] = {
        currentOptions: options,
        stagedOptions: {},
        initialChanges: {},
      }
      // reset the save options status/error
      state.saveOptions[viewIdString] = {
        status: Status.IDLE,
        error: null,
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveOptions.pending, (state, action) => {
        const { viewId } = action.meta.arg
        state.saveOptions[viewId.toString()] = {
          status: Status.LOADING,
          error: null,
        }
      })
      .addCase(saveOptions.fulfilled, (state, action) => {
        const { viewId } = action.meta.arg
        state.saveOptions[viewId.toString()] = {
          status: Status.SUCCEEDED,
          error: null,
        }
      })
      .addCase(saveOptions.rejected, (state, action) => {
        const { viewId } = action.meta.arg
        state.saveOptions[viewId.toString()] = {
          status: Status.FAILED,
          error: action.payload,
        }
      })
  },
})

export const { initializeView, stageOptions, resetStagedOptions, saveStagedOptions, saveOptionsComplete } =
  viewOptionsSlice.actions

export const selectBase = state => state.data.viewOptions

export const selectCurrentOptions = (state, viewId) => {
  const base = selectBase(state)
  const viewIdString = viewId.toString()
  return base?.options?.[viewIdString]?.currentOptions || {}
}

export const selectStagedOptions = (state, viewId) => {
  const base = selectBase(state)
  const viewIdString = viewId.toString()
  return base?.options?.[viewIdString]?.stagedOptions || {}
}

export const selectInitialChanges = (state, viewId) => {
  const base = selectBase(state)
  const viewIdString = viewId.toString()
  return base?.options?.[viewIdString]?.initialChanges || {}
}

export const selectSaveOptionsStatus = (state, viewId) => {
  const returnVal = get(selectBase(state), ['saveOptions', viewId.toString(), 'status']) || Status.IDLE
  return returnVal
}

export const selectSaveOptionsError = (state, viewId) => {
  const returnVal = get(selectBase(state), ['saveOptions', viewId.toString(), 'error']) || null
  return returnVal
}

export default viewOptionsSlice.reducer
